import _ from 'lodash';
import styled from 'styled-components';
import { Flex } from 'components/base/layout-components';
import { getColor } from 'common/constants/_colors';
import { SPACES } from 'common/constants/sizes';
import Text, { Inline } from 'components/base/Text';
import GitHubProviderImageExplain from 'images/screenshots/github-screenshot.png';
import GitlabProviderImageExplain from 'images/screenshots/gitlab-screenshot.png';
import BitBucketProviderImageExplain from 'images/screenshots/bitbucket-screenshot.png';
import { Circle } from '../components/Shapes';
import {
  LBBitbucketIcon,
  LBButton,
  LBGithubIcon,
  LBGitlabIcon
} from '@linearb/buzz';

const Footer = styled(Flex)`
  border-top: 1px solid ${getColor('catskillWhite')};
`;

const StyledExplanationImg = styled(Flex)`
  ${({ src }) => (src ? `background: url(${src}) no-repeat;` : ``)};
  background-size: contain;
  width: 100%;
  height: 100%;
`;
const NumberedText = ({ number, text }) => (
  <Flex pb='small'>
    <Circle textColor='chambray' border='chambray' color='white' size='1.5'>
      {number}
    </Circle>
    <Text size='large' pl={SPACES.smaller}>
      {text}
    </Text>
  </Flex>
);
const Note = styled(Flex)`
  background: ${getColor('zircon')};
  border: 1px solid ${getColor('fog')};
  border-radius: 4px;
`;
const providerImageMap = {
  github: LBGithubIcon,
  gitlab: LBGitlabIcon,
  bitbucket: LBBitbucketIcon,
  bitbucket_server: LBBitbucketIcon
};

const providerAuthImageMap = {
  github: GitHubProviderImageExplain,
  gitlab: GitlabProviderImageExplain,
  bitbucket: BitBucketProviderImageExplain
};

const providerNoteMap = {
  github:
    'Note: If your GitHub organization has a Request button, click Request to ask your admin for permission to connect to LinearB.' +
    ' Proceed with the setup process once you see a green V next to your org name indicating that access has been granted.'
};

export const showOAuthExplanationModal = (
  provider,
  BackComponent,
  onContinue,
  isReauth = false
) => {
  const Logo = providerImageMap[provider];

  return (
    <Flex direction='column' height='100%'>
      <Flex direction='column' pt='large' pl='big' pr='big' height='100%'>
        <div className='flex gap-2 items-center'>
          <Logo className='size-6' />
          <Text size='x-x-x-large' weight='300' color='black'>
            {isReauth ? 'Reauthorize' : 'Connect'} to {_.capitalize(provider)}
          </Text>
        </div>
        <Flex direction='row' pt='x-large' grow='1'>
          <Flex direction='column' grow='1' basis='0'>
            {showProviderSteps(provider)}
            <Flex pt='medium'>
              <LBButton
                data-cy='continue-to-provider'
                size='lg'
                onClick={() => onContinue(provider)}
              >
                {provider === 'github' ? 'Connect GitHub' : 'Continue'}
              </LBButton>
            </Flex>
            {!!providerNoteMap[provider] && (
              <Note mt='big' padding='small'>
                <Text size='medium'>{providerNoteMap[provider]} </Text>
              </Note>
            )}
          </Flex>
          <Flex direction='column' grow='1' basis='0' pl='big' pb='big'>
            <StyledExplanationImg
              provider={provider}
              src={providerAuthImageMap[provider]}
              alt={provider}
            />
          </Flex>
        </Flex>
      </Flex>
      {BackComponent && (
        <Footer padding='normal'>
          <Text size='medium'>{BackComponent}</Text>
        </Footer>
      )}
    </Flex>
  );
};

const showProviderSteps = provider => {
  switch (provider) {
    case 'github':
      return (
        <>
          <Text size='large' pb={SPACES.big}>
            Authorize LinearB to connect to GitHub in 3 clicks.
          </Text>
          <NumberedText number={1} text='Click button below to open GitHub' />
          <NumberedText
            number={2}
            text='Click the Grant button next to your GitHub organization'
          />
          <NumberedText number={3} text='Click Authorize linear-b' />
        </>
      );

    case 'bitbucket':
      return (
        <>
          <Text size='medium' pb={SPACES.small} lineHeight={SPACES.medium}>
            Click “
            <Inline size='medium' weight='bold'>
              Continue
            </Inline>
            ” in order to login and connect LinearB to your BitBucket account.
            After signing in to your BitBucket account you will be asked to
            “Grant Access” to LinearB. Click “Grant Access” in order to continue
            your sign up.
          </Text>
        </>
      );

    case 'gitlab':
      return (
        <>
          <Text size='medium' pb={SPACES.small} lineHeight={SPACES.medium}>
            Click “
            <Inline size='medium' weight='bold'>
              Continue
            </Inline>
            ” in order to login and connect LinearB to your GitLab account.
            After signing in to your GitLab account you will be asked to
            “Authorize” LinearB. Click “Authorize” in order to continue your
            sign up.
          </Text>
        </>
      );
    default:
      return null;
  }
};
